import { useEffect } from "react";
import { X } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { cardPublicUrl, cardShareUrl, CATEGORY_LABEL_KEYS } from "@/data/trainingCards";
import ShareActions from "@/components/cards/ShareActions";

/**
 * Lightbox modal showing the full-resolution card + share actions panel.
 *
 * Closes on ESC, on backdrop click, and on explicit X. Scroll-locks the
 * page body while open.
 */
export default function CardModal({ card, onClose }) {
  const { t, lang } = useT();

  useEffect(() => {
    if (!card) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [card, onClose]);

  if (!card) return null;

  const theme = card.theme[lang] || card.theme.en;
  const quote = card.quote[lang] || card.quote.en;
  const categoryLabel = t(CATEGORY_LABEL_KEYS[card.category]);
  // The PNG URL is always absolute (so share platforms can find it).
  const absoluteImage = `${window.location.origin}${cardPublicUrl(card)}`;
  const shareUrl = cardShareUrl(card);

  return (
    <div
      data-testid="card-modal"
      role="dialog"
      aria-modal="true"
      aria-label={`${theme}, ${card.attribution}`}
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6"
    >
      {/* backdrop */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        data-testid="card-modal-backdrop"
        className="absolute inset-0 bg-black/85 backdrop-blur-md"
      />

      {/* close button */}
      <button
        type="button"
        onClick={onClose}
        data-testid="card-modal-close"
        aria-label={t("cards.modal.close")}
        className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10 inline-flex items-center justify-center w-11 h-11 rounded-full bg-black/70 border border-gold/60 text-gold hover:bg-gold/15 hover:border-gold transition-colors"
      >
        <X size={18} strokeWidth={1.5} />
      </button>

      {/* content */}
      <div className="relative z-[1] grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-6 lg:gap-10 max-h-full w-full max-w-5xl">
        {/* Image column */}
        <figure className="flex items-center justify-center min-h-0">
          <img
            src={cardPublicUrl(card)}
            alt={`${theme}: ${quote} (${card.attribution})`}
            className="max-h-[78vh] w-auto h-auto max-w-full object-contain shadow-[0_40px_120px_-30px_rgba(0,0,0,0.95)] border border-gold/20"
            data-testid="card-modal-image"
          />
        </figure>

        {/* Side panel */}
        <aside
          className="relative bg-ink-elevated/90 backdrop-blur-md border border-white/10 p-6 lg:p-8 max-h-[78vh] overflow-y-auto"
          data-testid="card-modal-panel"
        >
          <p className="text-[10px] tracking-[0.32em] uppercase text-gold mb-3">
            {categoryLabel} · {theme}
          </p>
          <p className="font-serif italic text-bone text-xl md:text-2xl leading-snug">
            “{quote}”
          </p>
          <p className="mt-4 text-[11px] tracking-[0.24em] uppercase text-gold-soft">
            — {card.attribution}
          </p>

          <div className="mt-8 pt-6 border-t border-white/10">
            <ShareActions card={card} imageUrl={absoluteImage} shareUrl={shareUrl} />
          </div>
        </aside>
      </div>
    </div>
  );
}
